import type { Task, TimeDeviation, DeviationAggregation } from '@/types';

/**
* Calculate time deviation for a single task-assignee pair
*/
export function calculateTaskDeviation(
  task: Task,
  assignee: string
): TimeDeviation | null {
  // Get estimated time for this assignee
  const estimated = task.timeEstimatesByAssignee?.[assignee] || 0;

  // Skip if no estimate
  if (estimated === 0) return null;

  return {
  taskId: task.id,
  taskName: task.name,
  assignee,
  estimated,
  spent: 0,
  deviation: -estimated,
  deviationPercent: -100,
  };
}

/**
* Calculate all deviations for a task across all assignees
*/
export function calculateTaskDeviations(task: Task): TimeDeviation[] {
  const deviations: TimeDeviation[] = [];
  const assignees = new Set([
  ...(task.assignees || []),
  ...Object.keys(task.timeEstimatesByAssignee || {}),
  ]);

  for (const assignee of assignees) {
  const deviation = calculateTaskDeviation(task, assignee);
  if (deviation) {
      deviations.push(deviation);
  }
  }

  return deviations;
}

/**
* Calculate all deviations for a list of tasks
*/
export function calculateAllDeviations(tasks: Task[]): TimeDeviation[] {
  const allDeviations: TimeDeviation[] = [];

  for (const task of tasks) {
  const taskDeviations = calculateTaskDeviations(task);
  allDeviations.push(...taskDeviations);
  }

  return allDeviations;
}

/**
* Filter deviations based on filter type
*/
export function filterDeviations(
  deviations: TimeDeviation[],
  filter: 'all' | 'over' | 'under' | 'on-time',
  tolerancePercent: number = 5
): TimeDeviation[] {
  switch (filter) {
  case 'over':
      return deviations.filter(d => d.deviation > 0 && Math.abs(d.deviationPercent) > tolerancePercent);
  case 'under':
      return deviations.filter(d => d.deviation < 0 && Math.abs(d.deviationPercent) > tolerancePercent);
  case 'on-time':
      return deviations.filter(d => Math.abs(d.deviationPercent) <= tolerancePercent);
  case 'all':
  default:
      return deviations;
  }
}

/**
* Aggregate deviations by assignee
*/
export function aggregateByAssignee(deviations: TimeDeviation[]): DeviationAggregation[] {
  const byAssignee = new Map<string, TimeDeviation[]>();

  for (const deviation of deviations) {
  if (!byAssignee.has(deviation.assignee)) {
      byAssignee.set(deviation.assignee, []);
  }
  byAssignee.get(deviation.assignee)!.push(deviation);
  }

  return Array.from(byAssignee.entries()).map(([assignee, devs]) => {
  const totalEstimated = devs.reduce((sum, d) => sum + d.estimated, 0);
  const totalSpent = devs.reduce((sum, d) => sum + d.spent, 0);
  const totalDeviation = totalSpent - totalEstimated;
  const deviationPercent = totalEstimated > 0 ? (totalDeviation / totalEstimated) * 100 : 0;

  const overEstimateCount = devs.filter(d => d.deviation > 0).length;
  const underEstimateCount = devs.filter(d => d.deviation < 0).length;
  const onTimeCount = devs.filter(d => Math.abs(d.deviationPercent) <= 5).length;

  return {
      assignee,
      totalEstimated,
      totalSpent,
      totalDeviation,
      deviationPercent,
      taskCount: devs.length,
      overEstimateCount,
      underEstimateCount,
      onTimeCount,
  };
  });
}

/**
* Aggregate deviations by task
*/
export function aggregateByTask(deviations: TimeDeviation[]): DeviationAggregation[] {
  const byTask = new Map<string, TimeDeviation[]>();

  for (const deviation of deviations) {
  if (!byTask.has(deviation.taskId)) {
      byTask.set(deviation.taskId, []);
  }
  byTask.get(deviation.taskId)!.push(deviation);
  }

  return Array.from(byTask.entries()).map(([taskId, devs]) => {
  const totalEstimated = devs.reduce((sum, d) => sum + d.estimated, 0);
  const totalSpent = devs.reduce((sum, d) => sum + d.spent, 0);
  const totalDeviation = totalSpent - totalEstimated;
  const deviationPercent = totalEstimated > 0 ? (totalDeviation / totalEstimated) * 100 : 0;

  const overEstimateCount = devs.filter(d => d.deviation > 0).length;
  const underEstimateCount = devs.filter(d => d.deviation < 0).length;
  const onTimeCount = devs.filter(d => Math.abs(d.deviationPercent) <= 5).length;

  return {
      taskId,
      totalEstimated,
      totalSpent,
      totalDeviation,
      deviationPercent,
      taskCount: devs.length,
      overEstimateCount,
      underEstimateCount,
      onTimeCount,
  };
  });
}

/**
* Format deviation percentage with sign
*/
export function formatDeviationPercent(percent: number): string {
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(1)}%`;
}
